import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import _ from 'lodash';
import { Address, Login, School, User } from '../../interfaces/User';
import { PasswordPolicy } from '../../interfaces/password-policy';
import { CmsService } from '../../services/cms.service';
import { PasswordPolicyService } from '../../services/password-policy.service';
import { StorageService } from '../../services/storage.service';
import { UsersService } from '../../services/users.service';
import { ValidationService } from '../../services/validation.service';

@Component({
    selector: 'app-create-account-component',
    templateUrl: './create-account.component.html',
    styleUrls: ['./create-account.component.scss']
})
export class CreateAccountComponent implements OnInit {
    display = true;
    submitted = false;
    registering = false;

    user: User = new User();
    school: any = new School();
    address: any = new Address();
    errors: any[] = [];
    blocks: any;

    confirmPassword = '';

    regions: any[] = [];
    provinces: any[] = [];
    cities: any[] = [];
    passwordPolicy: PasswordPolicy | null = null;
    passwordPolicySummary = '';

    genderOption = [
        { label: 'Male', value: 'Male' },
        { label: 'Female', value: 'Female' },
    ];

    copyright = '© ' + moment().format('YYYY') + ' saferoads.ph version 1.9 Powered by Reviewmasters Inc.';

    constructor(
        private readonly validationService: ValidationService,
        private readonly cmsService: CmsService,
        private readonly userService: UsersService,
        private readonly passwordPolicyService: PasswordPolicyService,
        private readonly storageService: StorageService,
        private readonly router: Router,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.getAllRegions();
        this.user.gender = 'Male';
        this.loadContent();
        this.loadPasswordPolicy();
    }

    async loadPasswordPolicy(): Promise<void> {
        try {
            this.passwordPolicy = await this.passwordPolicyService.getPasswordPolicy();
            this.passwordPolicySummary = this.passwordPolicyService.getPolicySummary(this.passwordPolicy);
            this.cdr.detectChanges();
        } catch (err) {
            this.passwordPolicy = null;
            this.passwordPolicySummary = '';
        }
    }

    loadContent(): void {
        this.cmsService.getContent('register').then((res: any) => {
            const blocks: any = [];
            _.forEach(res.result, function (value: any) {
                blocks[value.block] = value.content;
            });
            this.blocks = blocks;
            this.cdr.detectChanges();
        });
    }

    validateEmail(email: string): boolean {
        const regularExpression =
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regularExpression.test(String(email || '').toLowerCase());
    }

    async register(): Promise<void> {
        const formIsValid =
            this.validationService.validateUser(this.user) &&
            this.validationService.validateAddress(this.address) &&
            this.validationService.validateSchoolDetails(this.school);

        if (formIsValid) {
            const passwordValidation = await this.passwordPolicyService.validatePassword({
                email: this.user.email,
                newPassword: this.user.password || '',
                confirmPassword: this.confirmPassword || '',
                scope: 'create-account',
            });

            this.passwordPolicy = passwordValidation.policy;
            this.passwordPolicySummary = this.passwordPolicyService.getPolicySummary(
                passwordValidation.policy
            );

            if (!passwordValidation.valid) {
                this.errors = [passwordValidation.message];
                this.submitted = true;
                this.cdr.detectChanges();
                return;
            }

            this.user.address = this.address;
            this.user.school = this.school;
            this.user.userType = 'SCHOOL';
            this.registering = true;
            this.errors = [];

            this.userService
                .register(this.user)
                .then((res: any) => {
                    if (res.statusCode === 200) {
                        const login: Login = new Login();
                        login.email = this.user.email;
                        login.password = this.user.password;

                        this.userService
                            .login(login)
                            .then(() => {
                                this.storageService.set('email', this.user.email).then(() => {
                                    location.reload();
                                });
                            })
                            .catch(() => {
                                this.registering = false;
                                this.cdr.detectChanges();
                            });
                    }

                    if (res.statusCode === 409 || res.statusCode === 404) {
                        this.errors = res.errorMessage;
                        this.registering = false;
                        this.cdr.detectChanges();
                    }
                })
                .catch((err) => {
                    this.registering = false;
                    console.error(err);
                    this.cdr.detectChanges();
                });
        }

        this.submitted = true;
    }

    getAllRegions(): void {
        this.cmsService.getRegions().then((res: any) => {
            if (res.statusCode === 200) {
                this.regions = res.result;
                this.regions.push({ regionName: null });
                this.regions = _.map(this.regions, function (value: any) {
                    return {
                        label: value.regionName ? value.regionName : '',
                        value: value.regionName,
                    };
                });
                this.cdr.detectChanges();
            }
        });
    }

    changeRegion(event: any): void {
        this.cmsService.getProvincesByRegionName(event.value).then((res: any) => {
            if (res.statusCode === 200) {
                this.provinces = res.result;
                this.provinces.push({ stateName: null });
                this.provinces = _.map(this.provinces, function (value: any) {
                    return {
                        label: value.stateName ? value.stateName : '',
                        value: value.stateName,
                    };
                });
                this.address.province = null;
                this.address.city = null;
                this.cities = [];
                this.cdr.detectChanges();
            }
        });
    }

    changeProvince(event: any): void {
        this.cmsService.getCitiesByProvinceName(event.value).then((res: any) => {
            if (res.statusCode === 200) {
                this.cities = res.result;
                this.cities.push({ cityName: null });
                this.cities = _.map(this.cities, function (value: any) {
                    return {
                        label: value.cityName ? value.cityName : '',
                        value: value.cityName,
                    };
                });
                this.address.city = null;
                this.cdr.detectChanges();
            }
        });
    }

    navigateToLogin(): void {
        void this.router.navigate(['/login']);
    }
}
